import type { IContact, IRecipient } from '../../common/recipients/types';
import type { EnvelopeStatusType, ResourceNames, SignerStatusType } from '../../lib/constants';

// TODO: A conflicting ISigner type exists at: packages/sender/src/common/recipients/types.ts
export interface ISigner {
  signerId: string;
  email: string;
  firstName: string;
  // Set by UPDATE_RECIPIENTS_DONE in the reducer, so it is here at runtime even
  // though it was missing from this type.
  middleName?: string;
  lastName?: string;
  signingStatus: SignerStatusType;
  color?: string;
  signingGroup?: string;
  role?: string;
  title?: string;
  recipientColorCounter: number;
}

export interface ICarbonCopyRecipient {
  id: string;
  envelopeId: string;
  role: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  color: string;
}

export type BlockType =
  | 'Signature'
  | 'Initials'
  | 'DateSigned'
  | 'FullName'
  | 'Text'
  | 'Checkbox'
  | 'Multiple'
  | 'Strike'
  | 'TimeSigned'
  | 'Rectangle';

export interface IBlock {
  blockId?: string;
  pageNumber: number;
  blockType: BlockType;
  value?: string | boolean;
  width?: number;
  height?: number;
  required?: boolean;
  x?: number;
  y?: number;
  assignedTo?: string | null;
  readOnly?: boolean;
  fontSize?: number;
  groupId?: string;
  isEditingDisabled: boolean;
  isCreating?: boolean;
  color?: string;
  bgColor?: string;
  opacity?: number;
  strikeType?: string;
  strikeShape?: string;
  previouslyAssignedTo?: string;
  documentId?: string;
  allowOptionalOverride?: boolean;
}

export enum StrikeType {
  Up = 'UP', // this represents a diagonal line going from bottom left to top right, /
  Down = 'DOWN', // this represents a diagonal line going from top left to bottom right, \
  /*
    strikeType value of `null` represents a horizontal line
  */
}

export enum StrikeShape {
  Line = 'LINE',
  Rectangle = 'RECTANGLE',
}

export interface IStrike extends IBlock {
  strikeType: StrikeType;
  strikeShape?: StrikeShape;
}

export interface IDocument {
  fileName: string;
  url?: string;
  documentId: string;
  numberOfPages?: number;
  blocks?: IBlock[];
  blockGroups?: IGroup[];
  isUploading?: boolean;
  isDownloaded: boolean;
  uploadStatus: DocumentStatus;
  presignedURL?: string;
  // Template Document Properties
  order?: number;
  links?: { downloadPDF?: { href?: string } };
  metadata?: Record<string, string>;
}

export enum DocumentStatus {
  Uploading = 'Uploading',
  Uploaded = 'Uploaded',
  Failed = 'Failed',
}

export interface IConfigs {
  senderUi?: {
    sendRedirectUrl?: string;
    envelopePathPrefix?: string;
    isBackButtonVisible: boolean;
    infoBox?: {
      title?: string;
      body?: string;
    };
  };
  signerUi?: {
    eSignAgreementUrl?: string;
  };
  areLegalLinksVisible: boolean;
  automatedRemindersEnabled?: boolean;
  dateFormat?: string;
}

export interface IEmailConfig {
  subject: string | null;
  body: string | null;
  loading: boolean;
}

export interface IEnvelope {
  envelopeId: string;
  emailConfig: IEmailConfig;
  documents: IDocument[];
  expiration: Date;
  carbonCopy: ICarbonCopyRecipient[];
  signers: ISigner[];
  status: EnvelopeStatusType;
  signingGroups?: ISigningGroup[];
  config?: IConfigs;
  recipientColorCounter?: number;
  accountId?: string;
}

export interface ProcessedEnvelope extends IEnvelope {
  blocks: IBlocks;
  groups: IGroups;
  signers: ISigner[];
  signingGroups: ISigningGroup[];
  carbonCopy: ICarbonCopyRecipient[];
  pageDimensions: IPageDimensions;
}

export interface ICopyCache {
  blocks: IBlock[];
  pasteCounter: number;
}

export interface IBlocks {
  [documentId: string]: IBlocksByDocument;
}
export interface IBlocksByDocument {
  [pageIndex: number]: IBlock[];
}

export interface ISelectedBlocks {
  documentId?: string;
  pageIndex?: number;
  blockIndices: number[];
  blockTypes?: string[];
  x?: number;
  y?: number;
}

export interface ICopiedBlocks {
  blocks?: IBlock[];
  startX?: number;
  startY?: number;
}

export interface IGroup {
  blockGroupType: string;
  id: string;
  documentId: string;
  envelopeId: string;
  pageNumber: number;
  blockIds: string[];
  validation: {
    rule: 'gte' | 'lte' | 'eq' | 'bt';
    value: number;
    valueB?: number;
  };
}
export interface ISigningGroup {
  name: string;
  order: number;
  id: string;
  links?: any[];
}

export interface IPageDimensions {
  [documentId: string]: {
    [pageIndex: number]: {
      height: number;
      width: number;
    };
  };
}

export interface IGroups {
  [documentId: string]: {
    [pageNumber: number]: {
      [groupId: string]: IGroup;
    };
  };
}

export interface IBlockSizingCache {
  [blockType: string]: {
    height: number;
    width: number;
  };
}

export interface ISenderBuildState {
  resource?: ResourceNames;
  id?: string;
  documents: IDocument[];
  zoom: number;
  blocks: IBlocks;
  groups: IGroups;
  activeBlockType: string;
  activeStrikeShape: StrikeShape;
  activeStrikeColor: string;
  activeSigner: string;
  selectedBlocks: ISelectedBlocks;
  copiedBlocks: ICopiedBlocks;
  saving: boolean;
  done: boolean;
  isEditingField: boolean;
  signers: ISigner[];
  carbonCopy: ICarbonCopyRecipient[];
  signingGroups: ISigningGroup[];
  pageDimensions: IPageDimensions;
  emailConfig: IEmailConfig;
  fetchError: boolean;
  config: IConfigs;
  isAutoSaving: undefined | boolean;
  copyCache: ICopyCache;
  inputFocused: boolean;
  currentPage: number | undefined;
  currentDocument: string | undefined;
  recipientsModal: IRecipients;
  recipientsFetched: boolean;
  lastPlacedBlock: string;
  isTextBlockEditingEnabled: boolean;
  status: string;
  signerToken: string;
  isDeletingBlocks?: boolean;
  isCreatingBlock?: boolean;
  mobileSigningOrderOpen: boolean;
  blockSizingCache: IBlockSizingCache;
  readyToSendModalOpen: boolean;
  recipientColorCounter?: number;
  mouseCoords?: { x: number | null; y: number | null };
  blockMenuPos: { x: number | null; y: number | null };
  autoBlocksLoading: boolean;
  accountId?: string;
  applyTemplateToDocumentId: string | null;
}

export interface IRecipients {
  recipients: IRecipient[];
  signingGroups: ISigningGroup[];
  loading: boolean;
  contacts: IContact[];
  saveSuccess?: boolean;
}

export interface ITemplateQueryParams {
  state: string;
  role: string;
  flashCodes: string[];
}

export enum FormsBackPage {
  Envelopes = 'envelopes',
  Fill = 'fill',
}

export type AutoBlock = IBlock & {
  transactionRole?: string;
};

export type AutoBlocksRes = {
  transactionRoles: string[];
  documents: Array<{
    documentId: string;
    status: 'pending' | 'done' | 'error';
    blocks: AutoBlock[];
  }>;
};
